/////////////////////////////////////////////////////////////////////////////////////////////////////////
//  1. 프로그램명 : 모달용(팝업) Provider
//  2. 프로그램ID : CommonModal.tsx
//  3. 주요설명 : 공통팝업
//  4. 개발이력 : 2025.08.17  최초개발
/////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { useState, useCallback, useMemo, useRef, useEffect, Suspense } from "react";
import { Modal } from "@mui/material";
import axios from 'axios';
import { Button, MenuItem, Stack, Paper, IconButton } from '@mui/material';
import InputField from './InputField';
import Tooltip from "@mui/material/Tooltip";
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 사용자정의 모듈
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { API_BASE_URL } from '../config'; // API 기본 URL
import '../styles/popupComponent.css';
import { useCommonTranslation } from '../translations';
import { PgmAuthGate } from '../utils/comptMapUtil'; // 팝업 열기 전 권한 확인(권한그룹에 없으면 403 페이지)
import NaviMenuBar from "./NaviMenuBar";
import { useResizable } from './ResizablePopup';   // 팝업 크기조절(2026-10-18)
import { getDateFormat, hasChanges, withDDHeader } from '../utils/commonUtils';
import { formatNumber } from '../utils/commonUtils';
import { useComboBox, useComboBoxBizUnit, useComboBoxSite } from '../hooks/useComboBox';
import { title } from "process";
// 공통팝업모달 - Context/타입/훅은 ModalContext.ts로 분리됨(AgGrid.tsx와의 순환참조 방지).
//   기존 화면코드 호환을 위해 아래에서 그대로 재수출(re-export)함.
import {
  ModalContext,
  PopupPgmContext,   // 팝업 안 NaviMenuBar의 [DD생성]용 팝업 프로그램 정보
  PopupCloseContext, // 팝업 안 NaviMenuBar가 [닫기]를 그리면 제목줄 [닫기]를 숨김
  type ModalCommonPopupOptions,
  type ModalUserPopupOptions,
  type ModalRefPopupOptions,
  type ModalMsgBoxOptions,
} from './ModalContext';

////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// ag-Grid 필요한 모듈만 등록 : ModuleRegistry
///     .ag-Grid v34 이상에서는 텍스트 셀 에디터(agTextCellEditor)를 사용하려면 TextEditorModule을 등록해야 합니다.
////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, ValueFormatterParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import AgGrid, { selectFirstRow, setInsertRow, setDeleteRow, setUpdateRow, setCopyRow, setCancelRow, setCellValue, getActiveCellValue, getRowCellValue } from "./AgGrid";    // agGrid 컴포넌트 import
import {  ModuleRegistry,  ClientSideRowModelModule,  CsvExportModule,
        TextEditorModule,  TextFilterModule,  NumberEditorModule,  NumberFilterModule,  SelectEditorModule,
        PaginationModule,  ValidationModule,  CellStyleModule,  ColumnAutoSizeModule,  RowStyleModule,  RowSelectionModule,  RowApiModule,
        // 필요시 추가 모듈
        _focusGridInnerElement} from 'ag-grid-community';

export { useCommonPopupModal, useUserPopupModal, useRefPopupModal, useMsgBoxModal } from './ModalContext';

ModuleRegistry.registerModules( [
        ClientSideRowModelModule,  CsvExportModule,
        TextEditorModule,  TextFilterModule,  NumberEditorModule,  NumberFilterModule,  SelectEditorModule,
        PaginationModule,  ValidationModule,  CellStyleModule,  ColumnAutoSizeModule,  RowStyleModule,  RowSelectionModule,  RowApiModule
        // 필요시 추가 모듈
    ]);

/////////////////////////////////////////////////////////////////////////////////////////////////////////
// 사용자/레퍼런스 팝업 틀(제목줄 + 내용) - 2026-09-28
//  - 팝업 안 메뉴바(NaviMenuBar)가 자기 [닫기](btnClose)를 그리면 PopupCloseContext로 알려주고, 그동안 제목줄 [닫기]는 숨김.
//  - 403(권한 없음)/404(페이지 없음)처럼 메뉴바가 없는 내용이거나, [닫기]가 없는 다른 내용이면 제목줄 [닫기]를 보여줌(닫을 방법이 없어지지 않게).
/////////////////////////////////////////////////////////////////////////////////////////////////////////
const PopupFrame = React.forwardRef<HTMLDivElement, { title: string; onClose: () => void; height?: string; width?: string; children: React.ReactNode }>(
    ({ title, onClose, height, width, children }, ref) => {
    const { ddKey } = useCommonTranslation();
    const [innerCloseCnt, setInnerCloseCnt] = useState(0);   // 팝업 안에서 [닫기]를 그린 메뉴바 수
    const reportInnerClose = useCallback((on: boolean) => setInnerCloseCnt(n => Math.max(0, n + (on ? 1 : -1))), []);
    // 크기조절(2026-10-18) : 오른쪽 아래 모서리 끌기 + 제목줄 [최대화/원래크기] - 바꾼 크기는 기억하지 않음
    const { ref: boxRef, sizeStyle, resetSize, handle } = useResizable(360, 240);
    const [maxed, setMaxed] = useState(false);
    const toggleMax = useCallback(() => { setMaxed(v => !v); resetSize(); }, [resetSize]);
    const setRefs = useCallback((el: HTMLDivElement | null) => {
        boxRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = el;
    }, [ref, boxRef]);
    const boxStyle: React.CSSProperties = maxed ? { width: '98vw', height: '96vh' } : { height: `${height || '75%'}`, width: `${width || '60%'}`, ...sizeStyle };
    return (
        <Stack ref={setRefs} className="popup-container" style={boxStyle}>
            {/* 제목 영역 - 제목줄 더블클릭도 최대화/원래크기 */}
            <Paper className="panel" variant="outlined" onDoubleClick={toggleMax}>
              <div className="row w-100">
                <span className="popup-title">{title}</span>
                <Tooltip title={maxed ? (ddKey('lblRestoreSize') || '원래크기') : (ddKey('lblMaximize') || '최대화')}>
                    <IconButton size="small" onClick={toggleMax} onDoubleClick={e => e.stopPropagation()}>
                        <i className={`fas ${maxed ? 'fa-compress' : 'fa-expand'}`} style={{ fontSize: 14 }}></i>
                    </IconButton>
                </Tooltip>
                {innerCloseCnt === 0 && (
                    <Tooltip title={ddKey('lblClose') || '닫기'}>
                        <span className="text-right">
                            <Button className='btnConfirm' variant="outlined" size="small" onClick={onClose}>
                                <i className="fas fa-xmark"></i>
                                <span className="btn-label-ellipsis">{ddKey('btnClose') || '닫기'}</span>
                            </Button>
                        </span>
                    </Tooltip>
                )}
              </div>
            </Paper>
            <PopupCloseContext.Provider value={reportInnerClose}>
                {children}
            </PopupCloseContext.Provider>
            {!maxed && handle}
        </Stack>
    );
});
PopupFrame.displayName = 'PopupFrame';

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // 공통라벨 번역
    const { ddKey } = useCommonTranslation();

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 1. 팝업 정의
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 공통팝업 상태 및 showCommonPopup 함수 추가
    const [commonPopupOpts, setCommonPopupOpts] = useState<ModalCommonPopupOptions>(null);
    const [isCommonPopup, setIsCommonPopup] = useState(false);
    // 선택된 행 상태 추가
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    // 공통팝업 호출
    const showCommonPopup = (configCommonPopup: Exclude<ModalCommonPopupOptions, null>) => {
      setCommonPopupOpts(configCommonPopup);
      setIsCommonPopup(true);
      setGrid1([]);         // AgGrid 데이터 초기화

      setFilters({
        inputCodeValue: configCommonPopup?.sqlWhereInputCodeValue || '',
        inputNameValue: configCommonPopup?.sqlWhereInputNameValue || ''
      });                  // 검색조건 초기화
      setLoading(false);   // 로딩상태 초기화
    };
    // 공통팝업 닫기
    const hideCommonPopup = () => {
      setCommonPopupOpts(null);
      setIsCommonPopup(false);
      setGrid1([]);         // AgGrid 데이터 초기화
      setFilters({
        inputCodeValue: '',
        inputNameValue: ''
      });                  // 검색조건 초기화
      setLoading(false);   // 로딩상태 초기화
    };

    // 사용자 팝업 상태 및 showUserPopup 함수 추가
    const [userPopupOpts, setUserPopupOpts] = useState<ModalUserPopupOptions | null>(null);
    const [isUserPopup, setIsUserPopup] = useState(false);
    // 사용자팝업 호출
    const showUserPopup = (configUserPopup: Exclude<ModalUserPopupOptions, null>) => {
    setUserPopupOpts(configUserPopup);
    setIsUserPopup(true);
    };
    // 사용자팝업 닫기
    const hideUserPopup = () => {
    setIsUserPopup(false);
    setUserPopupOpts(null);
    };

    // 레퍼런스 팝업 상태 및 showRefPopup 함수 추가
    //  - 스택(배열)으로 관리함: 이미 열려있는 레퍼런스팝업 내부(예: 플래너팝업의 그리드)에서 또 다른
    //    레퍼런스팝업(예: 패턴팝업)을 열어도 기존 팝업을 덮어쓰지 않고 위에 쌓이며, 닫기를 누르면
    //    맨 위(가장 최근에 연) 팝업만 닫혀서 이전 팝업으로 돌아감. AgGrid 우클릭메뉴 [OOO 팝업 열기]가
    //    팝업 안의 그리드에서도 동일하게 동작하면서 발견된 문제(단일 상태였을 때는 새 팝업이 기존 팝업을
    //    덮어써서, 닫기를 누르면 이전 팝업으로 안 돌아가고 전부 닫혀버렸음)를 해결하기 위함.
    //  - showRefPopup()/hideRefPopup() 시그니처는 그대로라서 이걸 호출하는 기존 코드는 수정 불필요.
    const [refPopupStack, setRefPopupStack] = useState<Exclude<ModalRefPopupOptions, null>[]>([]);
    // 레퍼런스 팝업 호출 - 스택 맨 위에 추가(push)
    const showRefPopup = (configRefPopup: Exclude<ModalRefPopupOptions, null>) => {
    setRefPopupStack(prev => [...prev, configRefPopup]);
    };
    // 레퍼런스 팝업 닫기 - 스택 맨 위(가장 최근에 연 팝업) 1개만 제거(pop)
    const hideRefPopup = () => {
    setRefPopupStack(prev => prev.slice(0, -1));
    };

    // 알림용 MsgBox 열기 함수    
    const [isMsgBox, setIsMsgBox] = useState(false);
    const [msgBoxOpts, setMsgBoxOpts] = useState<ModalMsgBoxOptions | null>(null);
    // 메시지박스(확인) 모달
    const showMsgBox = (o: ModalMsgBoxOptions) => {
        setMsgBoxOpts(o);
        setIsMsgBox(true);
    };
    // 메시지박스(확인) 모달 닫기
    const hideMsgBox = () => {
        setIsMsgBox(false);
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 1. 그리드 정의
    //      - 숫자 컬럼만 type: 'numericColumn'을 사용하세요.
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 1.1. agGrid1 - 그리드1
    // 1.1.1 그리드1 정의 및 Rows 및 Cols정의
    const agGrid1 = useRef<AgGridReact<any>>(null);
    const [agGrid1Rows, setGrid1] = useState<any[]>([]);
    const agGrid1Cols = useMemo<ColDef[]>(() => {
        // commonPopupOpts?.gridCellName과 gridCellCode가 있으면 동적으로 생성
        if (commonPopupOpts?.gridCellName && commonPopupOpts?.gridCellCode) {
            // gridCellName: { code: "코드", name: "명" }
            // gridCellCode: { code: "item_cd", name: "item_nm" }
            return [
                // {
                //     headerName: 'No',
                //     field: 'No',
                //     type: 'numericColumn',
                //     width: 80,
                //     sortable: false,
                //     filter: false,
                //     valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value, { format: '###', suffix: '', }),
                // },
                ...Object.keys(commonPopupOpts.gridCellCode).map((key) => ({
                    headerName: commonPopupOpts.gridCellName?.[key] || key,
                    field: commonPopupOpts.gridCellCode?.[key] || key,
                    width: commonPopupOpts.gridCellWidth?.[key] || 150,
                    sortable: true,
                    filter: true,
                }))
            ];
        }
        // 기본 컬럼
        return withDDHeader([
            //// { headerName: 'No', field: 'No', type: 'numericColumn', width: 80, sortable: false, filter: false, valueFormatter: (params: ValueFormatterParams) => formatNumber(params.value, { format: '###', suffix: '', }) },
            { headerName: '코드', field: 'code', width: 100 },
            { headerName: '명', field: 'name', width: 300 },
        ], ddKey);
    // commonPopupOpts?.gridCellName, commonPopupOpts?.gridCellCode가 바뀔 때마다 재생성
    }, [commonPopupOpts?.gridCellName, commonPopupOpts?.gridCellCode, commonPopupOpts?.gridCellWidth, ddKey]);
    // 1.1.2 그리드1 기본 옵션 : 사용자정의
    const agGrid1ColsDefault = useMemo<ColDef>(() => ({
        resizable: true,
        filter: true,
        floatingFilter: false,
        sortable: true,
    }), []);

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 2. 콤보구성
    //    - 콤보박스 스크롤 자동생성 : COMMON_MENU_PROPS - maxHeight: 48 * 10
    //    - 공통콤보 : useComboBox
    //    - 사용자콤보 : useComboBoxSite
    //    - 사용법 :
    //      1) 원본 데이터는 useComboBox('HCODE', includeAll)(또는 useComboBoxSite)로 한 번만 받고,
    //      2) 그리드 컬럼에 붙일 땐 ...comboColDef(options) — 원본 {value,label} 배열이 필요하니까 그대로 넘김,
    //      3) TextField(select) 드롭다운에 붙일 땐 {comboMenuItems(options)} — JSX 렌더링.
    // 2.1 공통상수로 콤보 속정정의 - 콤보박스의 스크롤 자동생성하기
    const COMMON_MENU_PROPS = {
        MenuProps: {
            PaperProps: {
            style: {
                maxHeight: 48 * 10,
            },
            },
        },
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 3. 페이지로딩시 기본값 설정
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 3.1 공통값 정의

    // 3.2 기본값정의 : 사용자정의
    const [filters, setFilters] = useState({
        inputCodeValue: '',
        inputNameValue: ''
    });

    // 3.2 기본값정의 : 사용자정의
    //  조건의 기본값을 설정하는 곳
    //  JumpIn할때 파라미터로 받은 값을 조건부에 설정하는 곳
    //      - autoQuery가 true이면 fncQuery() 실행
    useEffect(() => {
       setFilters(prev => ({
                ...prev,
                inputCodeValue: commonPopupOpts?.sqlWhereInputCodeValue || '',
                inputNameValue: commonPopupOpts?.sqlWhereInputNameValue || ''
            }));
    }, [commonPopupOpts?.sqlWhereInputCodeValue, commonPopupOpts?.sqlWhereInputNameValue]);

    // autoQuery로 자동조회
    useEffect(() => {
        if (commonPopupOpts?.autoQuery) {
            fncQuery();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [commonPopupOpts?.autoQuery, filters]);

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 4. 기능패널 : 버튼정의
    //      - 공통-Common 	: 조회(fncQuery), 저장(fncSave)
    //      - 싱글-Single 	: 신규(fncNew), 삭제(fncDelete), 복사(fncCopy)
    //      - 멀티-Grid 	: 행추가(fncInsertRow), 행삭제(fncDeleteRow), 행복사(fncCopyRow), 행아니오(fncCancelRow)
    //      - 도구-Tool 	: 초기화(fncClear), 도움말(fncHelpDoc), 엑셀저장(fncExportExcel), 미리보기(fncPreview), 추가검색토글(fncConditionToggle)
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 4.1 [공통-Common] ***************************************
    // 4.1.1 조회 : fncQuery,  <i className="fas fa-search w-4"></i>
    const fncQuery = useCallback(async () => {
        setLoading(true);
        try {
            await fetchDBQuery();   // return boolean(true/false)
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    // 4.1.2 저장 : fncSave, <i className="fas fa-save w-4"></i>
    const fncSave = useCallback(async () => {
        setLoading(true);
        try {
            await fetchDBSave({});   // return boolean(true/false)
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agGrid1Rows]);

    // 4.2 [싱글-Single] ***************************************
    // 4.2.1 신규 : fncNew
    const fncNew = useCallback(() => {
    }, []);

    // 4.2.2 삭제 : fncDelete
    const fncDelete = useCallback(() => {
    }, []);

    // 4.2.3 복사 : fncCopy
    const fncCopy = useCallback(() => {
    }, []);

    // 4.3 [멀티-Grid] ***************************************
    // 4.3.1 행추가 : fncInsertRow
    const fncInsertRow = useCallback(() => {
    }, []);

    // 4.3.2 행삭제 : fncDeleteRow
    const fncDeleteRow = useCallback(() => {
    }, []);

    // 4.3.3 행복사 : fncCopyRow, <i class="fa-solid fa-clone"></i>
    const fncCopyRow = useCallback(() => {
    }, []);

    // 4.3.4 행아니오 : fncCancelRow
    const fncCancelRow = useCallback(() => {
    }, []);
    // 4.4 [팝업] ***************************************
    // 4.4.1 확인 : fncConfirm
    const fncConfirm = useCallback(() => {
        // agGrid1에서 선택된 행 가져오기
        const selectedNodes = agGrid1.current?.api.getSelectedNodes();
        const selectedRowData = selectedNodes && selectedNodes.length > 0 ? selectedNodes[0].data : null;

        if (commonPopupOpts?.onConfirm) {
            commonPopupOpts.onConfirm(selectedRowData);
        }
        hideCommonPopup();
    }, [commonPopupOpts]);
    // 4.4.2 닫기 : fncClose
    const fncClose = useCallback(() => {
        hideCommonPopup();  // 모달 닫기  
    }, []);

    // 4.5 [도구-Tool] ***************************************
    const fncClear = () => {
        // 조건부 : 검색조건
        setFilters(
            { 
            // 검색조건 기본값 설정 ///////////////////////////////////////////////
              inputCodeValue: '',
              inputNameValue: ''
            // 검색조건 추가 ///////////////////////////////////////////////
        });
        // 자료부 : 그리드
        setGrid1([]);
    };

    // 4.5.2 도움말 : fncHelpDoc, <i className="fas fa-file-excel w-4"></i>
    const fncHelpDoc = useCallback(() => {
        showMsgBox({ title: ddKey('lblHelpDoc') || "도움말", msgText: ddKey('msgHelpDoc') || '도움말을 확인하세요.' });
    }, [ddKey]);

    // 4.5.3 엑셀로전환 : fncExportExcel, <i className="fas fa-file-excel w-4"></i>
    const fncExportExcel = useCallback(() => {
        // 파일명 : 페이지명_날짜(YYYYMMDDHHMMSS).확장자
        // 확장자 : xlsx, csv
        const pageName = '${pageName}';
        const fileName = `${pageName}_${getDateFormat('YYYYMMDDHHMMSS')}.csv`;
        showMsgBox({ title: ddKey('lblHelpDoc') || "도움말", msgText: ddKey('msgExportExcelDownload', { fileName }) || (fileName + '로 다운로드 됩니다.') });
        agGrid1.current?.api.exportDataAsCsv({ fileName });
    }, [ddKey]);

    // 4.5.4 미리보기 : fncPreview, <i className="fas fa-file-excel w-4"></i>
    const fncPreview = useCallback(() => {
        showMsgBox({ title: ddKey('lblHelpDoc') || "도움말", msgText: ddKey('msgPreview') || '미리보기를 확인하세요.' });
    }, [ddKey]);

    // 4.5.5 검색조건 토글 : fncConditionToggle, 표시/숨김 상태
    const fncConditionToggle = useCallback(() => {
    }, []);

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 5. 그리드 행/셀 이벤트(eventHandlers) 처리하기
    //      - Grid1의 이벤트 핸들러 : eventHandlersAgGrid1
    //      - Grid2의 이벤트 핸들러 : eventHandlersAgGrid2
    //      - 이벤트핸들러 종류
    //      - 행 클릭 핸들러 : onRowClicked()
    //      - 행 더블클릭 핸들러 : onRowDoubleClicked()
    //      - 셀 값 변경 핸들러 : onCellValueChanged()
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 5.1 Grid1의 이벤트 핸들러 : eventHandlersAgGrid1
    const eventHandlersAgGrid1 = {
        // ag-Grid의 onRowClicked 이벤트에 연결
        onRowClicked: (e: any) => {
            const cntrNo = e?.data?.cntr_no;
            if (cntrNo) {
                //fetchDBQuery2(cntrNo);
            }
        },
        onRowDoubleClicked: (e: any) => {
            // 더블클릭 시 onConfirm(=fncConfirm) 실행
            if (commonPopupOpts?.onConfirm) {
                commonPopupOpts.onConfirm(e.data); // 선택된 행 데이터를 onConfirm 콜백에 전달
            }
            hideCommonPopup();
            //setSelectedRow(e.data); // 선택된 행 저장
            //fncConfirm();
        },
        onCellValueChanged: (e: any) => {
            // 예시: 셀 값 변경시 동작
        },
    };
    // 5.2 Grid2의 이벤트 핸들러 : eventHandlersAgGrid2
    const eventHandlersAgGrid2 = {
        // ag-Grid의 onRowClicked 이벤트에 연결
        onRowClicked: (e: any) => {
            const cntrNo = e?.data?.cntr_no;
            if (cntrNo) {
                //fetchDBQuery2(cntrNo);
            }
        },
        // (필요시 다른 이벤트도 추가)
        onRowDoubleClicked: (e: any) => {
            // 예시: 더블클릭시 동작
        },
        onCellValueChanged: (e: any) => {
            // 예시: 셀 값 변경시 동작
        },
    };

    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 6. fetch ServerSide 처리 : 사용자정의
    //      - 조회 : fetchDBQuery()
    //      - 저장 : fetchDBSave()
    //      - 삭제 : fetchDBDelete()
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 6.1 fetchDBQuery() : 조회
    const fetchDBQuery = async () => { setGrid1([]);
        try {
            const rsRowData = await axios.post(`${API_BASE_URL}/com/apiCommonModal`, {
                sqlSelectStatements: commonPopupOpts?.gridCellCode, // string으로 변환된 값 사용
                sqlFromStatements: commonPopupOpts?.sqlFromStatements || '',
                sqlWhereStatements: commonPopupOpts?.sqlWhereStatements || '',
                sqlOrderbyStatements: commonPopupOpts?.sqlOrderbyStatements || '',
                filters,
            });
            //console.log('rsRowData:', rsRowData);
            const rsResults = rsRowData.data.rsResults;
            // 서버에서 오류 객체가 넘어오는 경우 처리
            if (rsResults === null) return false; // 조회결과 없음
            if (rsResults && typeof rsResults === 'object' && 'status' in rsResults && rsResults.status === 500) {
                const errMsg = rsResults.json?.details.error || (ddKey('msgDbQueryError') || 'DB 조회 중 오류 발생');
                const errDetail = rsResults.json?.details.originalError.info.message
                    ? (ddKey('msgErrorDetailPrefix') || '\n상세: ') + rsResults.json.details.originalError.info.message
                    : '';
                showMsgBox({ title: ddKey('lblError') || "오류", msgText: errMsg + errDetail });
                setGrid1([]);
                // autoQuery를 false로 변경
                setCommonPopupOpts(prev => prev ? { ...prev, autoQuery: false } : prev);
                return false;
            }
            setGrid1(rsResults || []);
            // autoQuery를 false로 변경
            setCommonPopupOpts(prev => prev ? { ...prev, autoQuery: false } : prev);
        } catch (error: any) {
            //console.error('DB 조회 오류:', error);
            showMsgBox({ title: ddKey('lblError') || "오류", msgText: (ddKey('msgDbQueryErrorCatch') || 'DB 조회 중 오류가 발생했습니다.\n') + (error?.message || error) });
            setGrid1([]);
            // autoQuery를 false로 변경
            setCommonPopupOpts(prev => prev ? { ...prev, autoQuery: false } : prev);
            return false;
        }
        return true;
    };

    // 6.2 fetchDBSave() : 저장
    const fetchDBSave = async (data: any) => {
        showMsgBox({ title: ddKey('lblAlertTitle') || "알림", msgText: ddKey('msgNoSaveData') || '[저장]할 데이터가 없습니다.', msgType:"info" });
        return true;
    };

    // 6.3 fetchDBDelete() : 직접삭제
    const fetchDBDelete = async (data: any) => {
        showMsgBox({ title: ddKey('lblAlertTitle') || "알림", msgText: ddKey('msgNoDeleteData') || '[삭제]할 데이터가 없습니다.', msgType:"info" });
        return true;
    };

    // console.log(paramPassData);
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 9. 화면정의
    //      - 메뉴바 영역 : naviMenuStack - 공통, 싱글, 멀티, 도구
    //      - 조건부 영역 : searchFormStack - 기본조건, 상세조건
    //      - 자료부 영역 : agGrid - agGrid1Stack, agGrid2Stack
    //      - 하단부 영역 : footerStack - Footer 영역
    /////////////////////////////////////////////////////////////////////////////////////////////////////////
    // 확정하는 메세지박스
    const confirmMsgBox = (opts: ModalMsgBoxOptions) => {
        return new Promise<boolean>((resolve) => {
            showMsgBox({...opts, btnYes: () => { resolve(true); }, btnNo: () => { resolve(false); } });
        });
    };
    
    // 공통팝업, 칩메세지
    return (
      <ModalContext.Provider value={{
        showCommonPopup,    // 공통팝업(보이기 )
        hideCommonPopup,    // 공통팝업(숨기기)
        showMsgBox,        // 메시지박스(보이기)
        hideMsgBox,        // 메시지박스(숨기기)
        confirmMsgBox,
        showUserPopup,        // 사용자 팝업(보이기)
        hideUserPopup,       // 사용자 팝업(숨기기)
        showRefPopup,         // 레퍼런스 팝업(보이기)
        hideRefPopup,        // 레퍼런스 팝업(숨기기)
      }}>
        {children}
        {/* 기존 모달 */}
                {isCommonPopup && (
                    <Modal open={isCommonPopup}  /*onClose={hideCommonPopup}*/
                            aria-labelledby="modal-modal-title"
                            aria-describedby="modal-modal-description"
                            className="modal-center">
                        {/* 기존 화면 전체를 모달 내부에 렌더링 */}
                        <PopupFrame title={commonPopupOpts?.title || ddKey('lblPopUp') || '팝업'} onClose={fncClose} width={`${commonPopupOpts?.modalWidth || '30%'}`}>{/* 2026-10-18 : 크기조절/최대화를 위해 공통 틀 사용(아래 메뉴바에 [닫기]가 있어 제목줄 [닫기]는 숨겨짐) */}
                                {/* 메뉴바 영역*/}
                                <Paper className="panel" variant="outlined">
                                        <div className="naviMenuStack row w-100">
                                            <NaviMenuBar
                            // 공통
                            naviMenuCommon={true}
                            btnQuery={{handler: fncQuery, show: true, disable: false }}
                            // 팝업
                            naviMenuPopup={true}
                            btnConfirm={{ handler: fncConfirm, show: true, disable: false }}
                            btnClose={{ handler: fncClose, show: true, disable: false }}
                            // 도구
                            naviMenuTools={false}
                            //btnClear={{ handler: fncClear, show: false, disable: loading }}
                            btnHelpDoc={{ handler: fncHelpDoc, show: true, disable: false }}
                            //btnExportExcel={{ handler: fncExportExcel, show: true, disable: !agGrid1Rows.length }}
                            btnPreview={{ handler: fncPreview, show: false, disable: false }}
                            //btnConditionToggle={{ handler: fncConditionToggle, show: false, disable: false }}
                            //rowCount={agGrid1Rows?.length ?? 0}
                            loading={loading}
                        />
                    </div>
                </Paper>              
                {/* 조건부 영역 */}
                <Paper className="panel" variant="outlined">
                  <div className="row">
                      <InputField className="inputCodeValue minw-200" type='text' label={ddKey('lblCode') || "코드"} size="small" value={filters.inputCodeValue}
                          onChange={e => setFilters(f => ({ ...f, inputCodeValue: e.target.value }))} />
                      <InputField className="inputNameValue minw-300" type='text' label={ddKey('lblName') || "명"} size="small" value={filters.inputNameValue}
                          onChange={e => setFilters(f => ({ ...f, inputNameValue: e.target.value }))} />
                  </div>
                </Paper>
                {/* 자료부 영역 : agGrid - flexDirection => 세로분할 : 'row', 가로분할 : 'column' */}
                <Paper className="panel panel-fill" variant="outlined">
                    {/* AgGrid1 */}
                    <div className="panel-fill">
                            <AgGrid
                                agGrid={agGrid1}
                                agGridRows={agGrid1Rows}
                                agGridCols={agGrid1Cols}
                                agGridColsDefault={agGrid1ColsDefault}
                                loading={loading}
                                pagination={true}
                                pageSize={1000}
                                pageSizeSelector={[20, 50, 100]}
                                rowSelection={{ type: "multiple" }}       // 단일선택:single, 다중선택:multiple
                                height={"100%"}   // 패널 내에서 가득 채우기
                                eventHandlers={eventHandlersAgGrid1}    // 행/셀 이벤트 핸들러 전달
                        />
                    </div>
                    {/* AgGrid2 */}
                </Paper>
            </PopupFrame>
          </Modal>
        )}

        {/* 사용자 팝업 렌더링 */}
        {isUserPopup && (
          <Modal open={isUserPopup}  /*onClose={hideCommonPopup}*/
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
              className="modal-center">
            {/* 기존 화면 전체를 모달 내부에 렌더링 */}
            <PopupFrame title={userPopupOpts?.title || ddKey('lblPopUp') || '팝업'} onClose={hideUserPopup} height={userPopupOpts?.modalHeight} width={userPopupOpts?.modalWidth}>
                {/* 자료부 영역 : agGrid - flexDirection => 세로분할 : 'row', 가로분할 : 'column' */}
                <Paper className="panel panel-fill panel-scroll" variant="outlined">
                {userPopupOpts?.callComponent
                    ? React.isValidElement(userPopupOpts.callComponent)
                    ? userPopupOpts.callComponent // 이미 ReactNode면 그대로 렌더링
                    : (() => {
                        const CallComponent = userPopupOpts.callComponent as React.ComponentType<any>;
                        // paramProps를 그대로 넘기면 자식이 { paramProps: {...} }를 통째로 받아버려서
                        // paramData 등 실제 필요한 prop을 못 받음(레퍼런스팝업의 paramProps 처리 방식과 동일하게 풀어서 전달)
                        // 팝업 프로그램ID : 권한 확인(403), DD 조회 범위(menuId), 팝업 메뉴바의 DD생성(PopupPgmContext)에 공통 사용
                        const pgmId = String(userPopupOpts.pgmId || (CallComponent as any).pgmId || '');
                        return (
                          <PgmAuthGate pgmId={userPopupOpts.skipAuthGate ? '' : pgmId} onClose={hideUserPopup}>
                            <PopupPgmContext.Provider value={pgmId ? { pgmId, callPgmId: userPopupOpts.callPgmId || `popup/${pgmId}` } : null}>
                              <Suspense fallback={null}>{/* [팝업호출](Z2005M1)처럼 지연로딩(lazy) 컴포넌트로 열릴 수 있어 감쌈 */}
                                <CallComponent {...{ ...(pgmId ? { menuId: pgmId } : {}), ...(userPopupOpts.paramProps || {}), autoQuery: userPopupOpts.autoQuery, onConfirm: userPopupOpts.onConfirm }} />
                              </Suspense>
                            </PopupPgmContext.Provider>
                          </PgmAuthGate>
                        );
                        })()
                    : null}
                </Paper>
            </PopupFrame>
          </Modal>
        )}

        {/* 레퍼런스 팝업 렌더링 - 스택에 쌓인 만큼 Modal을 겹쳐서 그림(나중에 연 팝업이 항상 맨 위) */}
        {refPopupStack.map((refPopupOpts, idx) => (
          <Modal key={idx} open={true}  /*onClose={hideCommonPopup}*/
              aria-labelledby="modal-modal-title"
              aria-describedby="modal-modal-description"
              className="modal-center">
            {/* 기존 화면 전체를 모달 내부에 렌더링 */}
            <PopupFrame title={refPopupOpts?.title || ddKey('lblPopUp') || '팝업'} onClose={hideRefPopup} height={refPopupOpts?.modalHeight} width={refPopupOpts?.modalWidth}>
                {/* 자료부 영역 : agGrid - flexDirection => 세로분할 : 'row', 가로분할 : 'column' */}
                <Paper className="panel panel-fill panel-scroll" variant="outlined">
                {refPopupOpts?.callComponent
                    ? React.isValidElement(refPopupOpts.callComponent)
                    ? refPopupOpts.callComponent // 이미 ReactNode면 그대로 렌더링
                    : (() => {
                        // AgGrid 우클릭메뉴에서 넘어오는 경우 React.lazy 컴포넌트일 수 있어 Suspense로 감쌈
                        // (이미 로드된 일반 컴포넌트는 즉시 렌더링되어 fallback이 보일 일이 없음)
                        const CallComponent = refPopupOpts.callComponent as React.ComponentType<any>;
                        // 팝업 프로그램ID : 권한 확인(403), DD 조회 범위(menuId), 팝업 메뉴바의 DD생성(PopupPgmContext)에 공통 사용
                        const pgmId = String(refPopupOpts.pgmId || (CallComponent as any).pgmId || '');
                        return (
                          // skipAuthGate : 팝업메뉴등록(Z2005M1) [팝업호출] - DD생성용이라 권한 배정 전 팝업도 열어봄(조회는 안 함, API는 서버가 계속 검사)
                          <PgmAuthGate pgmId={refPopupOpts.skipAuthGate ? '' : pgmId} onClose={hideRefPopup}>
                            <PopupPgmContext.Provider value={pgmId ? { pgmId, callPgmId: refPopupOpts.callPgmId || `popup/${pgmId}` } : null}>
                              <Suspense fallback={null}>
                                <CallComponent {...{ ...(pgmId ? { menuId: pgmId } : {}), ...(refPopupOpts.paramProps || {}), autoQuery: refPopupOpts.autoQuery }} />
                              </Suspense>
                            </PopupPgmContext.Provider>
                          </PgmAuthGate>
                        );
                        })()
                    : null}
                </Paper>
            </PopupFrame>
          </Modal>
        ))}

        {/* 알림용 MsgBox */}
        {isMsgBox && msgBoxOpts && (() => {
            const msgType = msgBoxOpts.msgType || 'info';
            const msgTypeIcon: Record<string, string> = {
              info: 'fa-circle-info',
              warning: 'fa-triangle-exclamation',
              error: 'fa-circle-xmark',
              success: 'fa-circle-check',
            };
            return (
              <Modal open={isMsgBox} /*onClose={hideMsgBox}*/ className="modal-center"
                  slotProps={{ backdrop: { sx: {
                      backgroundColor: 'rgba(15, 23, 42, 0.10)',
                      backdropFilter: 'blur(1px)',
                      WebkitBackdropFilter: 'blur(1px)',
                  } } }}>
                <div className="msg-box animate-modal-enter">
                    {/* 메세지 헤더 영역 : 아이콘 + 제목 */}
                    <div className="msg-header">
                      <span className={`msg-icon ${msgType}`}>
                        <i className={`fas ${msgTypeIcon[msgType] || msgTypeIcon.info}`}></i>
                      </span>
                      <div className="msg-content">
                        <p className="msg-title">{msgBoxOpts.title}</p>
                      </div>
                    </div>
                    {/* 메세지 내용 영역 */}
                    <p className="msg-text">{msgBoxOpts.msgText}</p>
                    {/* 메세지 상세 영역 */}
                    {msgBoxOpts.msgDetail && (
                        <div className="msg-detail-container">
                          <div className="msg-detail">{msgBoxOpts.msgDetail}</div>
                        </div>
                    )}
                    {/* 버튼 영역 */}
                    <div className="msg-buttons">
                        {/* btnOK: showBtn이 없거나 OK 포함일 때 항상 표시 */}
                        {(!msgBoxOpts.showBtn || msgBoxOpts.showBtn === 'OK' || msgBoxOpts.showBtn.includes('OK')) && (
                        <Tooltip title={ddKey('lblOk') || '확인'}>
                            <button className="msg-btn primary"
                                onClick={() => {
                                    msgBoxOpts.btnOK?.();
                                    hideMsgBox();
                                }}
                                >
                                {msgBoxOpts.confirmText || ddKey('btnOk') || '확인'}
                            </button>
                        </Tooltip>
                        )}
                        {/* btnYes: showBtn에 Yes 또는 YesNo 포함일 때 표시 */}
                        {(msgBoxOpts.showBtn && (msgBoxOpts.showBtn === 'Yes' || msgBoxOpts.showBtn === 'YesNo')) && (
                        <Tooltip title={ddKey('lblYes') || '예'}>
                            <button className="msg-btn primary"
                                onClick={() => {
                                    msgBoxOpts.btnYes?.();
                                    hideMsgBox();
                                }}
                                >
                                {msgBoxOpts.confirmText || ddKey('btnYes') || '예'}
                            </button>
                        </Tooltip>
                        )}
                        {(msgBoxOpts.showBtn && (msgBoxOpts.showBtn === 'No' || msgBoxOpts.showBtn === 'YesNo')) && (
                        <Tooltip title={ddKey('lblNo') || '아니오(N)'}>
                            <button className="msg-btn secondary"
                                onClick={() => {
                                    msgBoxOpts.btnNo?.();
                                    hideMsgBox();
                                }}
                                >
                                {msgBoxOpts.cancelText || ddKey('btnNo') || '아니오'}
                            </button>
                        </Tooltip>
                        )}
                    </div>
                </div>
              </Modal>
            );
        })()}
      </ModalContext.Provider>
    );
};

